<script lang="ts" setup>
    import ProductCarousel from "@/components/ProductCarousel.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { type PropType, ref, computed, inject } from "vue";
    // import { route } from "ziggy-js";
    import type { Cta } from "@/helpers/builder-init/index";
    import * as themeColors from "@assets/styles/colors.module.scss";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import { richTextHasText, useRichTextFormatting } from "@/helpers/composables/builderFieldUtility.ts";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    interface BannerMedia {
        desktop_media: string;
        mobile_media: string;
        alt_text: string;
        gradient_color: string;
        min_height: Number;
        max_height: Number;
        aria_label: string;
    }
    interface Eyebrow {
        text: string;
        color: string;
    }
    interface Heading {
        text: string;
        color: string;
        font_size_mobile: Number;
        font_size_desktop: Number;
    }

    let props = defineProps({
        banner_media: {
            type: Object as PropType<BannerMedia>,
            default() {
                return {};
            },
            required: true
        },
        eyebrow: {
            type: Object as PropType<Eyebrow>,
            default() {
                return { text: "" };
            },
            required: true
        },
        heading: {
            type: Object as PropType<Heading>,
            default() {
                return {};
            },
            required: true
        },
        tag: {
            type: String,
            default: ""
        },
        isNew: {
            type: Boolean,
            default: false
        },
        autoplay: {
            type: [String, Number],
            default: 0
        },
        cta: {
            type: Object as PropType<Cta>,
            default() {
                return {};
            },
        },
        allowQuickView: {
            type: Boolean,
            default: false
        }
    });
    let route = inject("route");
    const productCarouselRef = ref();

    const textColorClass = computed(() => {
        return {
            eyebrow: `text-${props.eyebrow.color}`,
            heading: `text-${props.heading.color}`
        };
    });

    const cssVars = computed(() => {
        let r,g,b;
        let gradientColor = themeColors[props.banner_media.gradient_color] ?? themeColors['rothcoBlackBlue'];
        if(gradientColor){
            r = parseInt(gradientColor.substring(1, 3), 16);
            g = parseInt(gradientColor.substring(3, 5), 16);
            b = parseInt(gradientColor.substring(5, 7), 16);
        }
        return {
            "--featured-product-carousel-gradient": gradientColor ? `${r}, ${g}, ${b}` : undefined
        };
    });

    let { optimize } = useImageOptimization();
</script>
<template>
    <div class="featured-products" :style="cssVars">
        <div
            v-if="props.banner_media.desktop_media"
            class="featured-products-bg"
        >
            <img
                :src="optimize(props.banner_media.desktop_media)"
                :alt="props.banner_media.alt_text"
                class="desktop-hidden"
            />
            <img
                :src="
                    optimize(props.banner_media.mobile_media
                        ? props.banner_media.mobile_media
                        : props.banner_media.desktop_media)
                "
                :alt="props.banner_media.alt_text"
                class="mobile-hidden"
            />
        </div>
        <section class="featured-carousel-container">
            <div class="featured-carousel-header">
                <span
                    v-if="eyebrow.text"
                    class="eyebrow"
                    :class="['eyebrow', textColorClass.eyebrow]"
                    >{{ eyebrow.text }}</span
                >
                <span
                    v-if="richTextHasText(heading.text)"
                    v-html="useRichTextFormatting(heading.text)"
                    :class="['headline-xl', textColorClass.heading]"
                ></span>
                <RothcoCta
                v-if="cta.label && tag"
                :href="route('productListing.collection', [tag])"
                cta-type="link"
                :class="cta.style"
                :aria-label="cta.aria_label"
            >
                {{ cta.label }}
            </RothcoCta>
            </div>
            <product-carousel
                featured
                ref="productCarouselRef"
                :tag="props.tag"
                :showNavButtons="true"
                :autoplay="props.autoplay"
                :togglePadding="true"
                :allowQuickView="props.allowQuickView"
                :is-new="props.isNew"
                :carouselClasses="{
                    section: 'featured-carousel-section',
                    body: 'carousel-list',
                    item: 'featured-carousel-item'
                }"
            />
        </section>
    </div>
</template>
